import { createMemoryRepository } from './repository'
import {
  ARTIFACT_TYPES,
  RELATION_TYPES,
  type ArtifactType,
  type Cluster,
  type MemoryArtifact,
  type MemoryEvent,
  type MemoryRelation,
  type MemoryRepository,
  type MemoryUniverse,
  type Position3D,
  type Project,
  type RelationType,
} from './types'

export interface GeneratorOptions {
  seed?: string
  projectCount?: number
  clusterCount?: number
  artifactCount?: number
  relationCount?: number
}

const DEFAULTS = { seed: 'loci-demo-v1', projectCount: 6, clusterCount: 30, artifactCount: 2500, relationCount: 10000 }
const PALETTE = ['#4fa9b7', '#d4ae58', '#d98573', '#62a98e', '#b8d9e7', '#d7d5cc']
const PROJECT_NAMES = ['Commerce Platform', 'Observability', 'Customer Experience', 'Data Foundation', 'Release Engineering', 'Research Lab']
const CLUSTER_NAMES = ['Checkout Flow', 'Incident Review', 'API Contracts', 'Runtime Metrics', 'User Interviews', 'Experiment Notes']
const TOPICS = ['checkout timeout', 'cache invalidation', 'payment retry', 'queue latency', 'search ranking', 'release rollback', 'session expiry', 'schema migration']
const BASE_TIME = Date.UTC(2025, 0, 1)
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))
const ARM_COUNT = 4

const hash = (value: string): number => {
  let result = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index)
    result = Math.imul(result, 16777619)
  }
  return result >>> 0
}

const random = (seed: string, key: string): number => hash(`${seed}:${key}`) / 0x1_0000_0000
const integer = (seed: string, key: string, max: number): number => Math.floor(random(seed, key) * max)
const round = (value: number): number => Math.round(value * 100) / 100
const clusterPositionFor = (seed: string, id: string, index: number): Position3D => {
  const arm = index % ARM_COUNT
  const ring = Math.floor(index / ARM_COUNT)
  const radius = 6 + ring * 6.5 + random(seed, `${id}:radius`) * 1.1
  const angle = arm * (Math.PI * 2 / ARM_COUNT) + Math.log(radius / 6) * 1.7 + (random(seed, `${id}:angle`) - 0.5) * 0.25
  return { x: round(Math.cos(angle) * radius), y: round(Math.sin(angle) * radius * 0.48), z: 0 }
}
const artifactPositionFor = (seed: string, id: string, cluster: Cluster): Position3D => {
  const radius = 0.7 + random(seed, `${id}:radius`) * 5.8
  const angle = Math.atan2(cluster.position.y / 0.48, cluster.position.x)
  const tangent = (random(seed, `${id}:tangent`) - 0.5) * radius * 2
  const radial = (random(seed, `${id}:radial`) - 0.5) * radius * 0.4
  return {
    x: round(cluster.position.x + Math.cos(angle) * radial - Math.sin(angle) * tangent),
    y: round(Math.max(-26, Math.min(26, cluster.position.y + (Math.sin(angle) * radial + Math.cos(angle) * tangent) * 0.48))),
    z: round((random(seed, `${id}:z`) - 0.5) * 7),
  }
}
const stateFor = (type: ArtifactType): MemoryArtifact['state'] => {
  if (type === 'Error') return 'open'
  if (type === 'Decision' || type === 'Test') return 'resolved'
  if (type === 'Dataset' || type === 'Document') return 'archived'
  return 'active'
}
const artifactFields = (seed: string, id: string, cluster: Cluster, type: ArtifactType): Pick<MemoryArtifact, 'path' | 'state' | 'importance' | 'position'> => ({
  path: `/${cluster.projectId}/${cluster.id}/${id}`,
  state: stateFor(type),
  importance: round(0.15 + random(seed, `${id}:importance`) * 0.85),
  position: artifactPositionFor(seed, id, cluster),
})
const iso = (offset: number): string => new Date(BASE_TIME + offset * 3_600_000).toISOString()

const addRelation = (
  relations: MemoryRelation[],
  known: Set<string>,
  sourceId: string,
  targetId: string,
  type: RelationType,
  offset: number,
): boolean => {
  if (sourceId === targetId) return false
  const key = `${sourceId}:${targetId}:${type}`
  if (known.has(key)) return false
  known.add(key)
  relations.push({ id: `rel-${relations.length.toString().padStart(5, '0')}`, sourceId, targetId, type, createdAt: iso(offset) })
  return true
}

const addStoryline = (seed: string, index: number, projects: Project[], clusters: Cluster[], artifacts: MemoryArtifact[], relations: MemoryRelation[], known: Set<string>): void => {
  const project = projects[index % projects.length]!
  const cluster = clusters.find((item) => item.projectId === project.id)!
  const prefix = `story-${index + 1}`
  const steps: Array<[ArtifactType, string, string]> = [
    ['Error', `Checkout timeout reported in production ${index + 1}`, 'A customer checkout timeout occurred after the payment gateway exceeded its response budget.'],
    ['Conversation', `Incident conversation: checkout timeout ${index + 1}`, 'The on-call team discussed the checkout timeout, its customer impact, and the gateway trace.'],
    ['Decision', `Adopt bounded checkout retries ${index + 1}`, 'Decision: retry idempotent gateway requests once before returning a checkout timeout.'],
    ['Code', `Implement checkout retry guard ${index + 1}`, 'Added a bounded retry guard around the checkout gateway client.'],
    ['Test', `Test checkout timeout retry ${index + 1}`, 'Verifies the checkout timeout retry succeeds once and preserves idempotency.'],
  ]
  const ids: string[] = []
  for (const [step, title, content] of steps) {
    const id = `${prefix}-${step.toLowerCase()}`
    ids.push(id)
    artifacts.push({ id, projectId: project.id, clusterId: cluster.id, type: step, title, content, tags: ['checkout', 'timeout', 'storyline'], createdAt: iso(index), updatedAt: iso(index + 1), ...artifactFields(seed, id, cluster, step) })
  }
  addRelation(relations, known, ids[0]!, ids[1]!, 'discusses', index)
  addRelation(relations, known, ids[1]!, ids[2]!, 'motivates', index)
  addRelation(relations, known, ids[2]!, ids[3]!, 'resolves', index)
  addRelation(relations, known, ids[3]!, ids[4]!, 'tested_by', index)
  const oldDecision = `${prefix}-old-decision`
  artifacts.push({ id: oldDecision, projectId: project.id, clusterId: cluster.id, type: 'Decision', title: `Legacy checkout timeout policy ${index + 1}`, content: 'The original policy returned the first gateway timeout without retry.', tags: ['checkout', 'timeout', 'superseded'], createdAt: iso(index - 1), updatedAt: iso(index), ...artifactFields(seed, oldDecision, cluster, 'Decision'), supersededBy: ids[2] })
  addRelation(relations, known, ids[2]!, oldDecision, 'supersedes', index)
}

export const generateMemoryUniverse = (options: GeneratorOptions = {}): MemoryUniverse => {
  const settings = { ...DEFAULTS, ...options }
  const projectCount = Math.max(1, settings.projectCount)
  const clusterCount = Math.max(projectCount, settings.clusterCount)
  const artifactCount = Math.max(24, settings.artifactCount)
  const relationCount = Math.max(1, settings.relationCount)
  const projects = Array.from({ length: projectCount }, (_, index): Project => ({
    id: `project-${(index + 1).toString().padStart(2, '0')}`,
    name: PROJECT_NAMES[index % PROJECT_NAMES.length]!,
    description: `A deterministic demo project focused on ${TOPICS[index % TOPICS.length]}.`,
    color: PALETTE[index % PALETTE.length]!,
    position: { x: 0, y: 0, z: 0 },
  }))
  const clusters = Array.from({ length: clusterCount }, (_, index): Cluster => {
    const id = `cluster-${(index + 1).toString().padStart(2, '0')}`
    const project = projects[Math.min(projectCount - 1, Math.floor(index * projectCount / clusterCount))]!
    return { id, projectId: project.id, name: `${CLUSTER_NAMES[index % CLUSTER_NAMES.length]} ${Math.floor(index / CLUSTER_NAMES.length) + 1}`, description: `Working set for ${project.name}.`, position: clusterPositionFor(settings.seed, id, index) }
  })
  for (const project of projects) {
    const members = clusters.filter((cluster) => cluster.projectId === project.id)
    project.position = {
      x: round(members.reduce((sum, cluster) => sum + cluster.position.x, 0) / members.length),
      y: round(members.reduce((sum, cluster) => sum + cluster.position.y, 0) / members.length),
      z: 0,
    }
  }
  const artifacts: MemoryArtifact[] = []
  const relations: MemoryRelation[] = []
  const knownRelations = new Set<string>()
  for (let index = 0; index < 4; index += 1) addStoryline(settings.seed, index, projects, clusters, artifacts, relations, knownRelations)

  const remaining = artifactCount - artifacts.length
  const duplicateCount = Math.floor(remaining * 0.02)
  for (let index = 0; index < remaining; index += 1) {
    const id = `artifact-${(index + 1).toString().padStart(5, '0')}`
    const cluster = clusters[index % clusters.length]!
    const topic = TOPICS[index % TOPICS.length]!
    const type = ARTIFACT_TYPES[index % ARTIFACT_TYPES.length]!
    const duplicateOf = index < duplicateCount ? `artifact-${((index + 1 + duplicateCount) % remaining + 1).toString().padStart(5, '0')}` : undefined
    artifacts.push({
      id, projectId: cluster.projectId, clusterId: cluster.id, type, title: `${topic} ${type.toLowerCase()} ${index + 1}`,
      content: `Deterministic ${type.toLowerCase()} record about ${topic} for ${cluster.name}.`,
      tags: [topic.split(' ')[0]!, type.toLowerCase(), `batch-${index % 12}`],
      createdAt: iso(index + 20), updatedAt: iso(index + 21), ...artifactFields(settings.seed, id, cluster, type), ...(duplicateOf ? { duplicateOf } : {}),
    })
  }
  const orphanCount = Math.floor(artifacts.length * 0.03)
  const orphanIds = new Set(artifacts.slice(-orphanCount).map((artifact) => artifact.id))
  const connected = artifacts.filter((artifact) => !orphanIds.has(artifact.id))
  let attempt = 0
  while (relations.length < relationCount && attempt < relationCount * 10) {
    const source = connected[integer(settings.seed, `source:${attempt}`, connected.length)]!
    const target = connected[integer(settings.seed, `target:${attempt}`, connected.length)]!
    const type = RELATION_TYPES[integer(settings.seed, `type:${attempt}`, RELATION_TYPES.length)]!
    addRelation(relations, knownRelations, source.id, target.id, type, attempt + 20)
    attempt += 1
  }
  const events: MemoryEvent[] = artifacts.slice(0, Math.min(300, artifacts.length)).map((artifact, index) => ({
    id: `event-${(index + 1).toString().padStart(4, '0')}`, artifactId: artifact.id, kind: index % 3 === 0 ? 'created' : index % 3 === 1 ? 'updated' : 'linked', occurredAt: iso(index), summary: `${artifact.type} ${artifact.title}`,
  }))
  return { schemaVersion: '1.0', seed: settings.seed, projects, clusters, artifacts, relations, events }
}

export const createDemoRepository = (options: GeneratorOptions = {}): MemoryRepository => createMemoryRepository(generateMemoryUniverse(options))
